import { deleteData, getData, postData, putData } from "../api/api";
import type { ResourceQueryCallPage } from "../components/ResourceQueryStatsPanel";
import { QUERY_HISTORY_PAGE_SIZE } from "../components/ResourceQueryStatsPanel";

export type OpenApiMutationDraftStatus = "PENDING" | "APPLIED" | "REJECTED";

export type OpenApiMutationDraft = {
  id?: string;
  createdBy?: string;
  catalogId?: string;
  catalogName?: string;
  tag?: string;
  toolName?: string;
  operation?: string;
  summary?: string;
  pathArgs?: Record<string, unknown>;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  status?: OpenApiMutationDraftStatus;
  createdAt?: string;
  resolvedAt?: string;
  correlationId?: string;
  applyResult?: string;
  /** Chat payload mirrors anomaly draft fields for the shared panel. */
  targetType?: string;
  targetId?: string;
  targetIndex?: string;
};

export type OpenApiCatalogSummary = {
  id: string;
  name: string;
};

export type OpenApiTagSummary = {
  tag: string;
  operationCount?: number;
  sampleTools?: string[];
};

export type OpenApiCatalogAuthType =
  | "TOKEN_PASS"
  | "FIXED_TOKEN"
  | "BASIC"
  | "OAUTH"
  | "NONE";

export type OpenApiOAuthGrant = "CLIENT_CREDENTIALS" | "PASSWORD";

export type OpenApiCatalog = {
  id: string;
  name: string;
  openapiDocUrl: string;
  baseUrl: string;
  enabled: boolean;
  snapshotFetchedAt?: string;
  lastError?: string;
  hasSnapshot?: boolean;
  operationCount?: number;
  tagCount?: number;
  guideDocumentId?: string;
  ragStatus?: "UPLOADED" | "INGESTING" | "READY" | "FAILED" | null;
  ragError?: string;
  analyzedAt?: string;
  authType?: OpenApiCatalogAuthType;
  oauthGrant?: OpenApiOAuthGrant | null;
  basicUsername?: string | null;
  oauthTokenUrl?: string | null;
  oauthClientId?: string | null;
  oauthScopes?: string | null;
  oauthUsername?: string | null;
  fixedTokenSet?: boolean;
  basicPasswordSet?: boolean;
  oauthClientSecretSet?: boolean;
  oauthPasswordSet?: boolean;
  vpnProfileId?: string | null;
  vpnProfileName?: string | null;
  queryCallCount?: number;
  querySuccessCount?: number;
  queryFailureCount?: number;
  autoDisableEnabled?: boolean;
  autoDisableMinSuccessPercent?: number | null;
  autoDisableMinCalls?: number | null;
  queryStatsResetAt?: string | null;
  autoDisabledAt?: string | null;
};

export type OpenApiCatalogOperation = {
  method: string;
  path: string;
  operationId?: string | null;
  summary?: string | null;
  description?: string | null;
  tags?: string[];
};

export type OpenApiCatalogSavePayload = {
  name: string;
  openapiDocUrl: string;
  baseUrl: string;
  enabled: boolean;
  authType: OpenApiCatalogAuthType;
  oauthGrant?: OpenApiOAuthGrant | null;
  basicUsername?: string | null;
  oauthTokenUrl?: string | null;
  oauthClientId?: string | null;
  oauthScopes?: string | null;
  oauthUsername?: string | null;
  fixedToken?: string | null;
  basicPassword?: string | null;
  oauthClientSecret?: string | null;
  oauthPassword?: string | null;
  /** VPN profile id; "" clears the assignment. */
  vpnProfileId?: string;
  autoDisableEnabled?: boolean;
  autoDisableMinSuccessPercent?: number;
  autoDisableMinCalls?: number;
};

const CHAT_BASE = "/api/openapi";
const ADMIN_BASE = "/api/openapi-catalogs";
const DRAFT_BASE = "/api/openapi/mutation-drafts";

export const openapiCatalogService = {
  getDraft: (id: string) => getData(`${DRAFT_BASE}/${id}`) as Promise<OpenApiMutationDraft>,
  applyDraft: (id: string) => postData(`${DRAFT_BASE}/${id}/apply`, {}) as Promise<OpenApiMutationDraft>,
  rejectDraft: (id: string) => postData(`${DRAFT_BASE}/${id}/reject`, {}) as Promise<OpenApiMutationDraft>,

  listChatCatalogs: async (): Promise<OpenApiCatalogSummary[]> => {
    const data = await getData(`${CHAT_BASE}/catalogs`);
    if (!Array.isArray(data)) return [];
    return data
      .filter((row): row is Record<string, unknown> => row != null && typeof row === "object")
      .map((row) => ({
        id: String(row.id || "").trim(),
        name: String(row.name || row.id || "").trim(),
      }))
      .filter((row) => row.id.length > 0);
  },

  listTags: async (catalogId: string): Promise<OpenApiTagSummary[]> => {
    const data = await getData(`${CHAT_BASE}/catalogs/${encodeURIComponent(catalogId)}/tags`);
    if (!Array.isArray(data)) return [];
    return data
      .filter((row): row is Record<string, unknown> => row != null && typeof row === "object")
      .map((row) => {
        const sampleTools = Array.isArray(row.sampleTools)
          ? row.sampleTools.filter((t): t is string => typeof t === "string" && t.trim().length > 0)
          : undefined;
        return {
          tag: String(row.tag || "").trim(),
          operationCount: typeof row.operationCount === "number" ? row.operationCount : undefined,
          sampleTools,
        };
      })
      .filter((row) => row.tag.length > 0);
  },

  listToolsForTag: async (
    catalogId: string,
    tag: string
  ): Promise<Array<{ toolName: string; method?: string; path?: string; summary?: string; write?: boolean }>> => {
    const data = await getData(
      `${CHAT_BASE}/catalogs/${encodeURIComponent(catalogId)}/tags/${encodeURIComponent(tag)}/tools`
    );
    if (!Array.isArray(data)) return [];
    return data
      .filter((row): row is Record<string, unknown> => row != null && typeof row === "object")
      .map((row) => ({
        toolName: String(row.toolName || "").trim(),
        method: row.method != null ? String(row.method) : undefined,
        path: row.path != null ? String(row.path) : undefined,
        summary: row.summary != null ? String(row.summary) : undefined,
        write: row.write === true,
      }))
      .filter((row) => row.toolName.length > 0);
  },

  listAdmin: () => getData(ADMIN_BASE) as Promise<OpenApiCatalog[]>,
  getAdmin: (id: string) => getData(`${ADMIN_BASE}/${id}`) as Promise<OpenApiCatalog>,
  listOperations: async (id: string): Promise<OpenApiCatalogOperation[]> => {
    const data = await getData(`${ADMIN_BASE}/${encodeURIComponent(id)}/operations`);
    if (!Array.isArray(data)) return [];
    return data
      .filter((row): row is Record<string, unknown> => row != null && typeof row === "object")
      .map((row) => {
        const tags = Array.isArray(row.tags)
          ? row.tags.filter((t): t is string => typeof t === "string" && t.trim().length > 0)
          : [];
        return {
          method: String(row.method || "").trim().toUpperCase(),
          path: String(row.path || "").trim(),
          operationId: row.operationId != null ? String(row.operationId) : null,
          summary: row.summary != null ? String(row.summary) : null,
          description: row.description != null ? String(row.description) : null,
          tags,
        };
      })
      .filter((row) => row.method.length > 0 && row.path.length > 0);
  },
  create: (body: OpenApiCatalogSavePayload) => postData(ADMIN_BASE, body) as Promise<OpenApiCatalog>,
  update: (id: string, body: OpenApiCatalogSavePayload) =>
    putData(`${ADMIN_BASE}/${id}`, body) as Promise<OpenApiCatalog>,
  refresh: (id: string) => postData(`${ADMIN_BASE}/${id}/refresh`, {}) as Promise<OpenApiCatalog>,
  analyzeRag: (id: string) => postData(`${ADMIN_BASE}/${id}/analyze-rag`, {}) as Promise<OpenApiCatalog>,
  tagAnalyses: (id: string) =>
    getData(`${ADMIN_BASE}/${id}/tag-analyses`) as Promise<
      Array<{ tag: string; markdown?: string }>
    >,
  remove: (id: string) => deleteData(`${ADMIN_BASE}/${id}`) as Promise<void>,
  queryCalls: (id: string, page = 0, size = QUERY_HISTORY_PAGE_SIZE) =>
    getData(`${ADMIN_BASE}/${id}/query-calls?page=${page}&size=${size}`) as Promise<ResourceQueryCallPage>,
  resetQueryStats: (id: string) =>
    postData(`${ADMIN_BASE}/${id}/query-stats/reset`, {}) as Promise<OpenApiCatalog>,
};

/** Prefix the user message so the chat agent receives selected catalog/tag context. */
export function formatOpenApiMessagePrefix(selection: {
  catalogId?: string;
  catalogName: string;
  tag?: string;
} | null): string {
  if (!selection?.catalogName) return "";
  const idPart = selection.catalogId ? ` catalogId=${selection.catalogId}.` : "";
  const tagPart = selection.tag ? ` Tag: ${selection.tag}.` : "";
  return `OpenAPI katalog: ${selection.catalogName}.${idPart}${tagPart}`;
}
